import React, { useState, useEffect, SetStateAction, useCallback } from 'react';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICategory, ISubcategory } from '@/types/api/companies';
import { IOperationType } from '@/types/api/companies';
import { getValues, removeFalsyValues } from '@/utils/objectUtils';
import { occupationAreaSchema } from '@/validators/registerCompany';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ICompanyData } from './View';

const ActuationAreaCard = ({
  isSelected,
  title,
  description,
  onClick,
  icon,
}: {
  isSelected?: boolean;
  title: string;
  description: string;
  icon: JSX.Element;
  onClick: () => void;
}) => (
  <Box
    sx={{
      cursor: 'pointer',
      width: 'min(100%, 390px)',
      padding: '16px',
      borderRadius: '12px',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      color: isSelected ? 'primary.contrastText' : '#000000',
      backgroundColor: `primary.${isSelected ? 'main' : 'contrastText'}`,
      transition: 'all 125ms ease-in-out',
      userSelect: 'none',
      '&:hover': {
        filter: 'brightness(0.95)',
      },
    }}
    onClick={onClick}
  >
    <Box
      component="span"
      sx={{ display: 'flex', justifyContent: 'space-between' }}
    >
      {icon}
      <CheckIcon
        sx={{
          backgroundColor: isSelected ? '#FFFFFF' : '#F3F3F3',
          borderRadius: '50%',
          color: `primary.${isSelected ? 'main' : 'contrastText'}`,
        }}
      />
    </Box>
    <Typography variant="subtitle2">{title}</Typography>
    <Typography
      sx={{
        marginTop: '6px',
        fontSize: '12px',
        fontWeight: 300,
        opacity: '50%',
      }}
    >
      {description}
    </Typography>
  </Box>
);

interface ICategoryListProps {
  label: string;
  categories: CategoryWithSubcategories[];
  checkedCategories: CheckedCategories;
  toggleCategory: (category: ICategory, categories: ICategory[]) => void;
  toggleSubCategory: (
    subcategory: ISubcategory,
    category: ICategory,
    categories: ICategory[]
  ) => void;
}

const CategoryList = (props: ICategoryListProps) => {
  const {
    label,
    categories,
    checkedCategories,
    toggleCategory,
    toggleSubCategory,
  } = props;

  return (
    <>
      {categories.length > 0 && (
        <Box sx={{ width: '390px' }}>
          <Typography
            component="h3"
            variant="h6"
            sx={{ opacity: '50%', my: '24px' }}
          >
            {label}
          </Typography>

          {categories.map(category => (
            <Accordion
              data-testid="categories-accordion"
              key={category.id}
              disableGutters
              sx={{
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                sx={{
                  flexDirection: 'row-reverse',
                  padding: 0,
                  '& .MuiAccordionSummary-content': {
                    margin: '0 10px',
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    transform: 'rotate(-90deg)',
                    marginRight: '10px',
                    '&.Mui-expanded': {
                      transform: 'rotate(0deg)',
                    },
                  },
                }}
                expandIcon={<ExpandMoreIcon sx={{ color: '#000000' }} />}
                aria-controls="categories-content"
              >
                <FormControlLabel
                  label={category.name}
                  onClick={e => e.stopPropagation()}
                  onChange={() => {
                    toggleCategory(category, categories);
                  }}
                  checked={!!checkedCategories[category.id]}
                  control={
                    <Checkbox sx={{ padding: '0', marginRight: '10px' }} />
                  }
                />
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '0 0 0 54px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    marginBottom: '24px',
                  }}
                >
                  {category.subcategories.map(subcategory => (
                    <FormControlLabel
                      key={subcategory.id}
                      label={subcategory.name}
                      checked={
                        !!checkedCategories[category.id]?.subcategories?.some(
                          ({ id }) => id === subcategory.id
                        )
                      }
                      control={<Checkbox />}
                      onChange={() =>
                        toggleSubCategory(subcategory, category, categories)
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </>
  );
};

interface IOccupationAreaProps {
  companyData: ICompanyData;
  onAdvancedStep: () => void;
  setCompanyData: (value: SetStateAction<ICompanyData>) => void;
}

type CheckedCategories = Record<string, CategoryWithSubcategories>;

type CategoryWithSubcategories = ICategory & {
  subcategories: ISubcategory[];
};

const CategoryTypes: { [K in IOperationType]: IOperationType } = {
  product: 'product',
  service: 'service',
} as const;

const OccupationArea = ({
  onAdvancedStep,
  companyData,
  setCompanyData,
}: IOccupationAreaProps) => {
  const { setIsLoading } = useLoading();

  const [operationTypes, setOperationTypes] = useState<IOperationType[]>(
    companyData.operation_types || []
  );
  const [checkedCategories, setCheckedCategories] = useState<CheckedCategories>(
    groupCategoriesWithSubCategories(
      companyData.categories,
      companyData.subcategories
    )
  );
  const [productCategories, setProductCategories] = useState<
    CategoryWithSubcategories[]
  >([]);
  const [serviceCategories, setServiceCategories] = useState<
    CategoryWithSubcategories[]
  >([]);

  function groupCategoriesWithSubCategories(
    categories: ICategory[] = [],
    subcategories: ISubcategory[] = []
  ): CheckedCategories {
    const categoryAsObj = categories.reduce((acc, current) => {
      acc[current.id] = current as CategoryWithSubcategories;
      acc[current.id].subcategories = [];
      return acc;
    }, {} as CheckedCategories);

    subcategories.forEach(subcategory =>
      categoryAsObj[subcategory.category].subcategories.push(subcategory)
    );

    return categoryAsObj;
  }

  const hasAnySubcategoryChecked = (categoryId: number) =>
    (checkedCategories[categoryId]?.subcategories || []).length > 0;

  const toggleOperationTypes = (value: IOperationType) => {
    if (operationTypes.includes(value)) {
      setOperationTypes(prevState =>
        prevState.filter(operation_type => operation_type !== value)
      );
    } else {
      setOperationTypes(prevState => [...prevState, value]);
    }
  };

  const toggleCategory = (category: ICategory, categories: ICategory[]) => {
    const categoryAlreadyChecked = checkedCategories[category.id];

    if (categoryAlreadyChecked && hasAnySubcategoryChecked(category.id)) return;

    setCheckedCategories(prevState =>
      removeFalsyValues({
        ...prevState,
        [category.id]: !categoryAlreadyChecked
          ? {
              ...categories.find(({ id }) => id === category.id),
              subcategories: [],
            }
          : undefined,
      })
    );
  };

  const toggleSubcategory = (
    subcategory: ISubcategory,
    category: ICategory,
    categories: ICategory[] = []
  ) => {
    const subcategoryAlreadyChecked = checkedCategories[
      category.id
    ]?.subcategories?.find(({ id }) => id === subcategory.id);

    const categoryParentAlreadyChecked = checkedCategories[category.id];

    if (!categoryParentAlreadyChecked) toggleCategory(category, categories);

    setCheckedCategories(prevState => ({
      ...prevState,
      [category.id]: {
        ...prevState[category.id],
        subcategories: subcategoryAlreadyChecked
          ? prevState[category.id].subcategories.filter(
              ({ id }) => id !== subcategory.id
            )
          : prevState[category.id].subcategories.concat(subcategory),
      },
    }));
  };

  const handleSubmitSelectOccupationAreaForm = async () => {
    try {
      const filteredByOperationType = getValues(checkedCategories).reduce(
        (acc, current) => {
          if (current && operationTypes.includes(current.operation_type)) {
            const { subcategories, ...category } = current;
            acc.subcategories = acc.subcategories.concat(subcategories);
            acc.categories.push(category);
          }
          return acc;
        },
        {
          categories: [] as ICategory[],
          subcategories: [] as ISubcategory[],
        }
      );

      await occupationAreaSchema.validate({
        operationTypesLength: operationTypes.length,
        categoriesLength: filteredByOperationType.categories.length,
      });

      const companyUpdatedPayload: ICompanyData = {
        ...companyData,
        operation_types: operationTypes,
        categories: filteredByOperationType.categories,
        subcategories: filteredByOperationType.subcategories,
      };

      setCompanyData(companyUpdatedPayload);
      onAdvancedStep();
    } catch (err) {
      if (err instanceof Error) {
        Toast('error', err.message);
        return;
      }
    }
  };

  const getAllCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<{ results: CategoryWithSubcategories[] }>(
        '/api/company_categories/'
      );
      setProductCategories(
        data.results.filter(
          category => category.operation_type === CategoryTypes.product
        )
      );
      setServiceCategories(
        data.results.filter(
          category => category.operation_type === CategoryTypes.service
        )
      );
    } catch (err) {
      Toast(
        'error',
        'Ocorreu um erro na listagem de categorias tente novamente mais tarde'
      );
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    getAllCategories();
  }, [getAllCategories]);

  return (
    <>
      <Box component="section">
        <Typography
          component="h3"
          variant="h6"
          sx={{ opacity: '50%', marginBottom: '18px' }}
        >
          Selecione seu tipo de atuação
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '24px',
          }}
        >
          <ActuationAreaCard
            icon={<StoreMallDirectoryIcon />}
            title="Produtos"
            description="(Selecione essa opção para receber cotações de produtos)."
            isSelected={operationTypes.includes('product')}
            onClick={() => toggleOperationTypes('product')}
          />
          <ActuationAreaCard
            icon={<BuildOutlinedIcon />}
            title="Serviços"
            description="(Selecione essa opção para receber cotações de serviços)."
            isSelected={operationTypes.includes('service')}
            onClick={() => toggleOperationTypes('service')}
          />
        </Box>
      </Box>

      <Box
        sx={theme => ({
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '10%',
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
          },
        })}
      >
        {operationTypes.includes(CategoryTypes.product) && (
          <CategoryList
            label="Selecione as categorias de produtos"
            categories={productCategories}
            checkedCategories={checkedCategories}
            toggleCategory={toggleCategory}
            toggleSubCategory={toggleSubcategory}
          />
        )}

        {operationTypes.includes(CategoryTypes.service) && (
          <CategoryList
            label="Selecione as categorias de serviços"
            categories={serviceCategories}
            checkedCategories={checkedCategories}
            toggleCategory={toggleCategory}
            toggleSubCategory={toggleSubcategory}
          />
        )}
      </Box>

      <Button
        variant="contained"
        fullWidth
        data-testid="button-next-step"
        className="submitButton"
        onClick={handleSubmitSelectOccupationAreaForm}
      >
        Próximo
      </Button>
    </>
  );
};

export type { IOccupationAreaProps, CategoryWithSubcategories };
export default OccupationArea;
